"use client";

// Vercel-style CTA: primary button + a terminal snippet with a tool picker.
// Each tool swaps in the one-liner that gets that audience to first lookup.

import { useState } from "react";
import { Button, Dropdown } from "@heroui/react";
import { Terminal, Code, ChevronDown, CheckCircleFilled, Copy } from "@/components/icons";
import type { ComponentType, SVGProps } from "react";

type Tool = {
  id: string;
  label: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  command: string;
};

const TOOLS: Tool[] = [
  {
    id: "curl",
    label: "cURL",
    icon: Terminal,
    command: `curl https://developer.encrata.com/api/lookup -H "Authorization: Bearer YOUR_API_KEY" -d '{"e": "satya@microsoft.com"}'`,
  },
  {
    id: "python",
    label: "Python",
    icon: Code,
    command: "pip install encrata",
  },
];

export function AgentCta({ align = "center", curlCommand }: { align?: "center" | "start"; curlCommand?: string }) {
  const tools = curlCommand
    ? TOOLS.map((t) => (t.id === "curl" ? { ...t, command: curlCommand } : t))
    : TOOLS;
  const [tool, setTool] = useState<Tool>(tools[0]);
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(tool.command);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className={`heroui flex flex-col items-center gap-3 sm:flex-row ${align === "center" ? "justify-center" : "sm:justify-start"}`}>
      <Button
        variant="primary"
        className="h-10 min-h-10 w-full shrink-0 rounded-xl px-6 text-sm font-semibold sm:w-auto"
        render={(props) => (
          // eslint-disable-next-line @next/next/no-html-link-for-pages
          <a {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)} href="/signup" />
        )}
      >
        Start with 500 free credits
      </Button>

      {/* Terminal snippet with tool picker - sidebar-card surface in light, graphite in dark.
          rounded-[8px]: theme caps --radius-lg at 6px; HeroUI buttons render at 0.5rem. */}
      <div className="flex h-10 w-full min-w-0 items-center gap-1 rounded-[8px] border border-border-secondary bg-sidebar dark:bg-card pl-1.5 pr-1 text-left sm:w-auto sm:max-w-136">
        <Dropdown>
          <Dropdown.Trigger
            aria-label="Choose your tool"
            className="group flex h-7 shrink-0 cursor-pointer items-center gap-1.5 rounded-lg bg-white dark:bg-white/8 px-2.5 text-xs font-medium text-text-primary transition-colors hover:bg-white/70 dark:hover:bg-white/14"
          >
            <tool.icon className="size-3.5 text-text-tertiary" />
            {tool.label}
            <ChevronDown className="size-3 text-text-quaternary transition-transform duration-200 group-aria-expanded:rotate-180" />
          </Dropdown.Trigger>
          <Dropdown.Popover className="heroui min-w-44" placement="bottom start" offset={6}>
            <Dropdown.Menu
              onAction={(key) => setTool(tools.find((t) => t.id === String(key)) ?? tools[0])}
            >
              {tools.map((t) => (
                <Dropdown.Item key={t.id} id={t.id} textValue={t.label}>
                  <span className="flex items-center gap-2">
                    <t.icon className="size-3.5 text-text-tertiary" />
                    {t.label}
                  </span>
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown.Popover>
        </Dropdown>

        <span className="hide-scrollbar min-w-0 flex-1 overflow-x-auto whitespace-nowrap px-2 font-mono text-xs text-text-secondary">
          <span className="mr-1.5 select-none text-text-quaternary">$</span>
          {tool.command}
        </span>

        <button
          type="button"
          onClick={copy}
          aria-label="Copy command"
          className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-lg text-text-tertiary transition-colors hover:bg-black/5 hover:text-text-primary dark:hover:bg-white/10"
        >
          {copied ? <CheckCircleFilled className="size-3.5 text-success" /> : <Copy className="size-3.5" />}
        </button>
      </div>
    </div>
  );
}
